import * as React from "react";
import * as ReactDOM from "react-dom";
import IFramePortal from "../components/IFramePortal";
import PopoverInvitePortal from "../components/PopoverInvitePortal";
import InlineImageInvitePortal from "../components/InlineImageInvitePortal";
import InlineChatInviteView from "../components/InlineChatInviteView";
import Messenger from "../components/Messenger";
import DynamicButton from "../components/DynamicButton";
import DefaultHead from "../components/DefaultHead";
import Launcher from "../components/Launcher";
import EndpointContext from "../context/EndpointContext";
import ConfigContext from "../context/ConfigContext";
import EventTrackingService from "../services/EventTrackingService";

import {
  Message,
  ChatUIStage,
  SocialData,
  PopoverInvite,
  InlineImageInvite,
  InlineChatInvite,
  KBArticle,
  Call,
  EventTrackingType,
  LineCreator,
  QuickReplyButton,
  VisitorBotMessageType,
  DataField,
  InvitationType,
  ActionPopoverContent,
  SecureForm,
  Form,
  InlineInviteContentType
} from "../types";
import BubbleInviteLauncher from "../components/Launcher/BubbleInviteLauncher";
import StoreContainer from "../stores/StoreContainer";
import ErrorView from "../components/ErrorView";
import { useEffect, useMemo, useState } from "react";
import usePrevious from "../hooks/usePrevious";
import useChat from "../hooks/useChat";
import useDocumentTitle from "../hooks/useDocumentTitle";
import CallButton from "../components/CallButton";

interface Props {
  siteId: number;
  container: HTMLElement;
}

function App(props: Props) {
  const eyecatcherDismissLengthInSeconds = 7200;
  const waitingText =
    "Please wait while we find an agent available to call you";
  const onCallForText = "Call in progress";

  const [isExpanded, setIsExpanded] = useState(
    StoreContainer.FrameStore.isExpanded()
  );
  const [messages, setMessages] = useState(
    StoreContainer.ChatStore.getMessages()
  );
  const [agentRating, setAgentRating] = useState(
    StoreContainer.ChatStore.getAgentRating()
  );
  const { chatStage, setChatStage, setClosedByAgent } = useChat();
  const [queuePosition, setQueuePosition] = useState(0);
  const [timeInQueue, setTimeInQueue] = useState(0);
  const [estimatedWait, setEstimatedWait] = useState("0:20");
  const [unavailableHtml, setUnavailableHtml] = useState("");
  const [unavailableUrl, setUnavailableUrl] = useState("");
  const [agentsTyping, setAgentsTyping] = useState<Array<string>>([]);
  const [socialData, setSocialData] = useState(
    StoreContainer.OAuthStore.getSocialData()
  );
  const [secureForm, setSecureForm] = useState<SecureForm>(
    StoreContainer.ChatStore.getSecureForm()
  );
  const [showPopoverInvite, setShowPopoverInvite] = useState(false);
  const [popoverInvite, setPopoverInvite] = useState<PopoverInvite>(null);
  const [showInlineInvite, setShowInlineInvite] = useState(false);
  const [inlineImageInvite, setInlineImageInvite] =
    useState<InlineImageInvite>(null);
  const [inlineChatInvite, setInlineChatInvite] =
    useState<InlineChatInvite>(null);
  const [unreadMessages, setUnreadMessages] = useState<Array<Message>>([]);
  const [kbArticles, setKbArticles] = useState<Array<KBArticle>>(null);
  const [messagePreviewDisabled, setMessagePreviewDisabled] = useState(false);
  const [call, setCall] = useState<Call>(null);
  const [dynamicButtonContainer, setDynamicButtonContainer] = useState(
    StoreContainer.DynamicButtonStore.getContainer()
  );
  const [callButtonContainer, setCallButtonContainer] = useState(
    StoreContainer.CallButtonStore.getContainer()
  );
  const [isChatAvailable, setIsChatAvailable] = useState(
    StoreContainer.AvailabilityStore.isChatAvailableInternal()
  );
  const [eyecatcherDismissed, setEyecatcherDismissed] = useState(
    !!StoreContainer.CookieStore.get("eyecatcherDismissed")
  );
  const [launcherVisibility, setLauncherVisibility] = useState(
    StoreContainer.FrameStore.getLauncherVisibility()
  );
  const [config, setConfig] = useState(StoreContainer.SettingStore.getConfig());
  const [dataField, setDataField] = useState(
    StoreContainer.BotStore.getDataField()
  );
  const [form, setForm] = useState(StoreContainer.BotStore.getForm());
  const [quickReplies, setQuickReplies] = useState(
    StoreContainer.BotStore.getQuickReplies()
  );
  const [actionPopoverContent, setActionPopoverContent] =
    useState<ActionPopoverContent>(null);
  const [messageFormDisabled, setMessageFormDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const [resumingChat, setResumingChat] = useState(true);
  const [cobrowseState, setCobrowseState] = useState("Stopped");

  const previousChatStage = usePrevious(chatStage);
  useDocumentTitle(config);

  function postTrackableEvent(type: EventTrackingType) {
    if (!StoreContainer.EngagementStore.hasEventTracking()) {
      return;
    }

    EventTrackingService.post(
      StoreContainer.SettingStore.getSiteId(),
      StoreContainer.VisitorStore.getVisitorId(),
      type
    );
  }

  const chatIsAssigned = useMemo(() => {
    return (
      chatStage === ChatUIStage.Assigned ||
      chatStage === ChatUIStage.AssignedToBot
    );
  }, [chatStage]);

  function playNewMessageSound() {
    const fileName = StoreContainer.SettingStore.getConfig().NewMessageSound;
    if (!fileName) {
      return;
    }
    const audio = new Audio(
      `${StoreContainer.SettingStore.getSoundEndpoint()}/${fileName}`
    );
    audio.play();
  }

  function onCloseMessagePreview() {
    setMessagePreviewDisabled(true);
  }

  function onClickPopout() {
    StoreContainer.FrameStore.popout();
    postTrackableEvent(EventTrackingType.VisitorClickedPopoutButton);
  }

  function onDismissEyecatcher() {
    StoreContainer.CookieStore.set("eyecatcherDismissed", true, {
      expires: eyecatcherDismissLengthInSeconds
    });

    setEyecatcherDismissed(true);
  }

  function renderLauncherPortal() {
    if (launcherVisibility === "hidden") {
      console.log("hiding launcher: launcherVisibility is hidden");
      return null;
    }

    if (StoreContainer.SettingStore.isLauncherDisabled() && !chatIsAssigned) {
      console.log("hiding launcher: launcher is disabled");
      return null;
    }

    if (isExpanded) {
      console.log("hiding launcher: is expanded");
      return null;
    }

    if (StoreContainer.FrameStore.isPopOut()) {
      console.log("hiding launcher: is popout");
      return null;
    }

    if (showInlineInvite) {
      console.log("hiding launcher: show inline invite");
      return null;
    }

    if (!StoreContainer.EngagementStore.isChatVisible()) {
      console.log("hiding launcher: chat not visible");
      return null;
    }

    if (StoreContainer.SettingStore.isInlineDisabled()) {
      console.log("hiding launcher: inline disabled");
      return null;
    }

    return (
      <Launcher
        onExpand={() => StoreContainer.FrameStore.expandOrPopout()}
        onDismissEyecatcher={() => onDismissEyecatcher()}
        onCloseMessagePreview={() => onCloseMessagePreview()}
        eyecatcherDismissed={eyecatcherDismissed}
        unreadMessages={unreadMessages}
        chatIsAssigned={chatIsAssigned}
        isChatAvailable={isChatAvailable}
        agentHeadshotUrl={StoreContainer.ChatStore.getAgentHeadshotUrl()}
        messagePreviewDisabled={messagePreviewDisabled}
      />
    );
  }

  function onRetryFromError() {
    setErrorMessage(null);
    StoreContainer.EngagementStore.startEngagement();
  }

  function renderErrorView() {
    return (
      <ErrorView
        message={errorMessage}
        isPopout={StoreContainer.FrameStore.isPopOut()}
        onClickRetry={() => onRetryFromError()}
        onCollapse={() => this.setState({ isExpanded: false })}
      />
    );
  }

  async function onCloseChat() {
    await StoreContainer.ChatStore.closeChat();
  }

  function onPopoutBeforeUnload(e: any) {
    return (e.returnValue = "Are you sure you want to close the chat?");
  }

  async function onClose() {
    await StoreContainer.ChatStore.close();
    StoreContainer.EngagementStore.clearEngagement();
    StoreContainer.ParentWindowEventStore.removeEventListener(
      "beforeunload",
      onPopoutBeforeUnload
    );
    StoreContainer.FrameStore.collapseOrClose();
  }

  function onSendIntroMessage(message: string) {
    setChatStage(ChatUIStage.Loading);
    StoreContainer.EngagementStore.sendIntroMessage(message);
  }

  function onSendMessage(text: string) {
    switch (chatStage) {
      case ChatUIStage.Assigned:
        StoreContainer.ChatStore.sendMessage(text);
        return;
      case ChatUIStage.AssignedToBot:
        StoreContainer.BotStore.clearQuickReplies();
        StoreContainer.BotStore.clearDataField();
        StoreContainer.BotStore.clearForm();
        StoreContainer.ChatStore.sendBotMessage(
          VisitorBotMessageType.Progress,
          text,
          null
        );
        return;
      default:
        console.error("Incorrect stage when sending message");
    }
  }

  function onClickQuickReply(button: QuickReplyButton) {
    StoreContainer.BotStore.clearQuickReplies();
    switch (chatStage) {
      case ChatUIStage.Assigned:
        StoreContainer.ChatStore.sendMessage(button.label);
        return;
      case ChatUIStage.AssignedToBot:
        StoreContainer.ChatStore.sendBotMessage(
          VisitorBotMessageType.Progress,
          button.label,
          button.pathId,
          button.sentiment
        );
        return;
      default:
        console.error("Incorrect stage when clicking quick reply button");
    }
  }

  async function onSubmitDataField(value: string) {
    if (chatStage !== ChatUIStage.AssignedToBot) {
      console.error("Incorrect stage when submitting data field");
      return;
    }

    StoreContainer.BotStore.clearDataField();
    await StoreContainer.ChatStore.sendBotMessage(
      VisitorBotMessageType.Progress,
      value,
      null
    );
  }

  async function onSubmitForm(data: any) {
    if (chatStage !== ChatUIStage.AssignedToBot) {
      console.error("Incorrect stage when submitting form");
      return;
    }

    StoreContainer.BotStore.clearForm();
    await StoreContainer.ChatStore.sendBotForm(data);
  }

  async function onClickActionCard(name: string, data: string) {
    await StoreContainer.ActionCardStore.handleCardAction(name, data);
  }

  async function onCollapsePopoverContent() {
    setActionPopoverContent(null);
  }

  function renderMessenger() {
    return (
      <Messenger
        agentRating={agentRating}
        unavailableHtml={unavailableHtml}
        unavailableUrl={unavailableUrl}
        onSetAgentRating={async (rating) =>
          await StoreContainer.ChatStore.setAgentRating(rating)
        }
        agentName={StoreContainer.ChatStore.getAgentName()}
        secureForm={secureForm}
        agentHeadshotUrl={StoreContainer.ChatStore.getAgentHeadshotUrl()}
        messages={messages}
        queuePosition={queuePosition}
        timeInQueue={timeInQueue}
        estimatedWait={estimatedWait}
        onCollapse={() => StoreContainer.FrameStore.collapse()}
        onPopout={() => StoreContainer.FrameStore.popout()}
        onCloseChat={async () => await onCloseChat()}
        onClose={async () => await onClose()}
        onSendIntroMessage={onSendIntroMessage}
        onSendMessage={onSendMessage}
        onclickQuickReply={onClickQuickReply}
        onSubmitDataField={onSubmitDataField}
        onSubmitForm={onSubmitForm}
        onSubmitEmailAddress={(emailAddress) =>
          StoreContainer.ChatStore.emailVisitorTranscript(emailAddress)
        }
        onSubmitPreChatSurvey={(answers: any) =>
          StoreContainer.EngagementStore.submitPrechatSurvey(answers)
        }
        onSubmitPostChatSurvey={(answers: any) =>
          StoreContainer.EngagementStore.submitPostchatSurvey(answers)
        }
        onSubmitUnavailableSurvey={(answers: any) =>
          StoreContainer.EngagementStore.submitUnavailableSurvey(answers)
        }
        onTyping={(message: string) =>
          StoreContainer.ChatStore.handleVisitorTyping(message)
        }
        agentsTyping={agentsTyping}
        socialData={socialData}
        waitingText={waitingText}
        kbArticles={kbArticles}
        call={call}
        onCallForText={onCallForText}
        visitorId={StoreContainer.VisitorStore.getVisitorId()}
        engagementId={StoreContainer.EngagementStore.getEngagementId()}
        engagementApiEndpoint={StoreContainer.SettingStore.getEngagementApiEndpoint()}
        maxFileSizeBytes={StoreContainer.EngagementStore.getMaxFileSizeBytes()}
        fileSizeErrorMessage={StoreContainer.EngagementStore.getFileSizeErrorMessage()}
        siteId={props.siteId}
        onSetLineRating={StoreContainer.ChatStore.setLineRating}
        isPopout={StoreContainer.FrameStore.isPopOut()}
        onContinueFromKnowledgeBase={() =>
          StoreContainer.EngagementStore.continueFromKnowledgeBase()
        }
        onVisitorEndCall={() => StoreContainer.CallStore.visitorEndCall()}
        onResendMessage={(message: Message) =>
          StoreContainer.ChatStore.resendMessage(
            message,
            chatStage === ChatUIStage.AssignedToBot
          )
        }
        dataField={dataField}
        form={form}
        quickReplies={quickReplies}
        onClickActionCard={(name: string, data: string) =>
          onClickActionCard(name, data)
        }
        actionPopoverContent={actionPopoverContent}
        onCollapsePopover={() => onCollapsePopoverContent()}
        messageFormDisabled={messageFormDisabled}
        errorMessage={errorMessage}
        onRetryFromError={() => onRetryFromError()}
        cobrowseState={cobrowseState}
      />
    );
  }

  function renderPopoverInvite() {
    if (!showPopoverInvite) {
      return null;
    }

    return (
      <PopoverInvitePortal container={props.container} invite={popoverInvite} />
    );
  }

  async function onExpandBubbleInvite() {
    StoreContainer.InviteStore.accept();
    StoreContainer.InviteStore.closeInlineInvite();
    const botEnabled = StoreContainer.EngagementStore.isBotEnabled();
    const agentsPrioritized =
      StoreContainer.EngagementStore.areAgentsPrioritized();
    if (botEnabled && !agentsPrioritized) {
      StoreContainer.FrameStore.expand();
    } else {
      setInlineChatInvite({
        ...inlineChatInvite,
        type: InvitationType.Inline,
        contentType: InlineInviteContentType.Chat,
        fromBubble: true
      });
    }
  }

  function onCloseBubbleInvite() {
    StoreContainer.InviteStore.reject();
    onCloseMessagePreview();
  }

  function buildBubbleInvite() {
    return (
      <BubbleInviteLauncher
        messages={inlineChatInvite.messages}
        onExpand={onExpandBubbleInvite}
        onCloseMessagePreview={() => {
          onCloseBubbleInvite();
        }}
        agentHeadshotUrl={inlineChatInvite.authorHeadshotUrl}
        messagePreviewDisabled={messagePreviewDisabled}
      />
    );
  }

  function renderLauncherInvite() {
    if (!showInlineInvite) {
      return null;
    }

    if (isExpanded) {
      return null;
    }

    if (inlineImageInvite) {
      return (
        <InlineImageInvitePortal
          container={props.container}
          invite={inlineImageInvite}
        />
      );
    }

    if (inlineChatInvite) {
      if (inlineChatInvite.type === InvitationType.Bubble) {
        return buildBubbleInvite();
      }
    }
  }

  function renderInlineChatInvite() {
    if (!showInlineInvite) {
      return null;
    }

    if (isExpanded) {
      return null;
    }

    if (!inlineChatInvite) {
      return null;
    }

    if (
      inlineChatInvite.type === InvitationType.Inline &&
      inlineChatInvite.contentType === InlineInviteContentType.Chat
    ) {
      return (
        <InlineChatInviteView
          container={props.container}
          invite={inlineChatInvite}
          config={config}
        />
      );
    }
  }

  function renderMessengerPortalContent() {
    if (errorMessage) {
      return renderErrorView();
    }

    return renderMessenger();
  }

  function renderMessengerPortal() {
    if (StoreContainer.SettingStore.isInlineDisabled()) {
      return null;
    }

    const inviteContent = renderInlineChatInvite();

    if (
      !StoreContainer.FrameStore.isPopOut() &&
      !isExpanded &&
      !inviteContent
    ) {
      return null;
    }

    return ReactDOM.createPortal(
      <div className={"velaro-messenger-frame velaro-fixed velaro-animated"}>
        <IFramePortal head={<DefaultHead />}>
          {inviteContent || renderMessengerPortalContent()}
        </IFramePortal>
      </div>,
      props.container
    );
  }

  function renderStyle() {
    return ReactDOM.createPortal(<DefaultHead />, window.parent.document.head);
  }

  function renderDynamicButton() {
    if (!dynamicButtonContainer) {
      return null;
    }

    return ReactDOM.createPortal(
      <DynamicButton
        isChatAvailable={isChatAvailable}
        onClick={onClickPopout}
      />,
      dynamicButtonContainer
    );
  }
  function renderCallButton() {
    if (!callButtonContainer) {
      return null;
    }

    return ReactDOM.createPortal(
      <CallButton onClick={onClickPopout} />,
      callButtonContainer
    );
  }

  function getEndpoints() {
    return {
      images: StoreContainer.SettingStore.getImagesEndpoint()
    };
  }

  //event listeners
  useEffect(() => {
    function onExpand() {
      setIsExpanded(true);
      setUnreadMessages([]);
      setMessagePreviewDisabled(false);
      postTrackableEvent(EventTrackingType.VisitorClickedInline);
    }
    StoreContainer.FrameStore.onExpand(onExpand);

    function onCollapse() {
      setIsExpanded(false);
    }
    StoreContainer.FrameStore.onCollapse(onCollapse);

    function onLauncherVisibilityUpdate(launcherVisibility: string) {
      setLauncherVisibility(launcherVisibility);
    }
    StoreContainer.FrameStore.onLauncherVisibilityUpdate(
      onLauncherVisibilityUpdate
    );

    function onAvailabilityChanged() {
      setIsChatAvailable(
        StoreContainer.AvailabilityStore.isChatAvailableInternal()
      );
    }
    StoreContainer.AvailabilityStore.onAvailabilityChanged(
      onAvailabilityChanged
    );

    function onConfigChanged() {
      setConfig(StoreContainer.SettingStore.getConfig());
    }
    StoreContainer.SettingStore.onConfigChanged(onConfigChanged);

    function onReceiveIntroForm() {
      setChatStage(ChatUIStage.IntroForm);
    }
    StoreContainer.EngagementStore.onReceiveIntroForm(onReceiveIntroForm);
    function onReceiveSecureForm() {
      setSecureForm(StoreContainer.ChatStore.getSecureForm());
    }
    StoreContainer.ChatStore.onReceiveSecureForm(onReceiveSecureForm);

    function onReceivePreChatSurvey() {
      setChatStage(ChatUIStage.PreChatSurvey);
      postTrackableEvent(EventTrackingType.VisitorShownPrechatSurvey);
    }
    StoreContainer.EngagementStore.onReceivePreChatSurvey(
      onReceivePreChatSurvey
    );

    function onReceivePostChatSurvey(content: any) {
      setChatStage(ChatUIStage.PostChatSurvey);
      if (content && content.Survey) {
        // bug somewhere that sends down postchat survey content without a survey
        // haven't been able to repro, so just circumventing side affect for now
        postTrackableEvent(EventTrackingType.VisitorShownPostchatSurvey);
      }
    }
    StoreContainer.EngagementStore.onReceivePostChatSurvey(
      onReceivePostChatSurvey
    );

    function onReceiveReferralContent() {
      setChatStage(ChatUIStage.Referral);
    }
    StoreContainer.EngagementStore.onReceiveReferralContent(
      onReceiveReferralContent
    );

    function onChatClosed() {
      setChatStage(ChatUIStage.Loading);
    }
    StoreContainer.EngagementStore.onChatClosed(onChatClosed);

    function onLoading() {
      setChatStage(ChatUIStage.Loading);
    }
    StoreContainer.EngagementStore.onLoading(onLoading);
    StoreContainer.ChatStore.onLoading(onLoading);

    function onReceiveUnavailableSurvey() {
      setChatStage(ChatUIStage.UnavailableSurvey);
      postTrackableEvent(EventTrackingType.VisitorShownMissedchatSurvey);
    }
    StoreContainer.EngagementStore.onReceiveUnavailableSurvey(
      onReceiveUnavailableSurvey
    );

    function onReceiveUnavailableHtml(html: string) {
      setChatStage(ChatUIStage.UnavailableHtml);
      setUnavailableHtml(html);
    }
    StoreContainer.EngagementStore.onReceiveUnavailableHtml(
      onReceiveUnavailableHtml
    );

    function onReceiveUnavailableUrl(url: string) {
      setUnavailableUrl(url);
      setChatStage(ChatUIStage.UnavailableUrl);
    }
    StoreContainer.EngagementStore.onReceiveUnavailableUrl(
      onReceiveUnavailableUrl
    );

    function onPostchatSurveySubmitted() {
      setChatStage(ChatUIStage.PostChatSurveySubmitted);
    }
    StoreContainer.EngagementStore.onPostchatSurveySubmitted(
      onPostchatSurveySubmitted
    );

    function onUnavailableSurveySubmitted() {
      setChatStage(ChatUIStage.UnavailableSurveySubmitted);
    }
    StoreContainer.EngagementStore.onUnavailableSurveySubmitted(
      onUnavailableSurveySubmitted
    );

    function onReceiveKnowledgeBaseArticles(kbArticles: Array<KBArticle>) {
      setChatStage(ChatUIStage.KBResults);
      setKbArticles(kbArticles);
    }
    StoreContainer.EngagementStore.onReceiveKnowledgeBaseArticles(
      onReceiveKnowledgeBaseArticles
    );

    function onFailToCloseWindow() {
      setChatStage(ChatUIStage.CloseWindow);
    }
    StoreContainer.FrameStore.onFailToCloseWindow(onFailToCloseWindow);

    function onReceiveError(errorMessage: string) {
      setErrorMessage(errorMessage);
    }
    StoreContainer.EngagementStore.onReceiveError(onReceiveError);

    function onChatConnected() {
      if (StoreContainer.FrameStore.isPopOut()) {
        StoreContainer.ParentWindowEventStore.addEventListener(
          "beforeunload",
          onPopoutBeforeUnload
        );
      }
    }
    StoreContainer.ChatStore.onConnected(onChatConnected);

    function onChatDisconnected() {
      if (StoreContainer.FrameStore.isPopOut()) {
        StoreContainer.ParentWindowEventStore.removeEventListener(
          "beforeunload",
          onPopoutBeforeUnload
        );
      }
    }
    StoreContainer.ChatStore.onDisconnected(onChatDisconnected);

    function onChatQueued() {
      setChatStage(ChatUIStage.Queued);
      setQueuePosition(StoreContainer.ChatStore.getQueuePosition());
      setTimeInQueue(StoreContainer.ChatStore.getTimeInQueue());
      setEstimatedWait(StoreContainer.ChatStore.getEstimatedWait());
      postTrackableEvent(EventTrackingType.VisitorEnteredQueue);
    }
    StoreContainer.ChatStore.onChatQueued(onChatQueued);

    function onChatAssigned() {
      setChatStage(ChatUIStage.Assigned);
      setAgentRating(StoreContainer.ChatStore.getAgentRating());
      setClosedByAgent(false);
      setMessageFormDisabled(false);
    }
    StoreContainer.ChatStore.onChatAssigned(onChatAssigned);

    function onChatAssignedToBot() {
      setChatStage(ChatUIStage.AssignedToBot);
      setClosedByAgent(false);
      setMessageFormDisabled(!StoreContainer.BotStore.textRepliesEnabled());
    }
    StoreContainer.ChatStore.onChatAssignedToBot(onChatAssignedToBot);

    function onCallWaiting() {
      setChatStage(ChatUIStage.CallWaiting);
    }
    StoreContainer.CallStore.onCallWaiting(onCallWaiting);

    function onCallReceived(call: Call) {
      setCall(call);
      setChatStage(call.Answered ? ChatUIStage.Call : ChatUIStage.CallWaiting);
    }
    StoreContainer.CallStore.onCallReceived(onCallReceived);

    function onMessagesLoaded(messages: Array<Message>) {
      setMessages([...messages]);
    }
    StoreContainer.ChatStore.onMessagesLoaded(onMessagesLoaded);

    function onMessagesChanged(messages: Array<Message>) {
      setMessages([...messages]);
    }
    StoreContainer.ChatStore.onMessagesChanged(onMessagesChanged);

    function onReceiveMessage(message: Message) {
      setMessages([...StoreContainer.ChatStore.getMessages()]);

      if (!StoreContainer.FrameStore.isExpanded()) {
        setUnreadMessages((unreadMessages) => [...unreadMessages, message]);
      }

      if (
        message.lineCreator === LineCreator.Agent ||
        (message.lineCreator === LineCreator.Bot &&
          StoreContainer.BotStore.botAudioEnabled())
      ) {
        playNewMessageSound();
      }
    }
    StoreContainer.ChatStore.onReceiveMessage(onReceiveMessage);

    function onChangeAgentRating(agentRating: number) {
      setAgentRating(agentRating);
    }
    StoreContainer.ChatStore.onChangeAgentRating(onChangeAgentRating);

    function onUpdateQueuePosition(queuePosition: number) {
      setQueuePosition(queuePosition);
    }
    StoreContainer.ChatStore.onUpdateQueuePosition(onUpdateQueuePosition);

    function onAgentsTypingChange(agentsTyping: Array<string>) {
      setAgentsTyping(agentsTyping);
    }
    StoreContainer.ChatStore.onAgentsTypingChange(onAgentsTypingChange);

    function onAgentClose() {
      setClosedByAgent(true);
    }
    StoreContainer.ChatStore.onAgentClose(onAgentClose);

    function onRecieveSocialData(socialData: SocialData) {
      setSocialData(socialData);
    }
    StoreContainer.OAuthStore.onReceiveSocialData(onRecieveSocialData);

    function onClosePopoverInvite() {
      setShowPopoverInvite(false);
    }
    StoreContainer.InviteStore.onClosePopoverInvite(onClosePopoverInvite);

    function onCloseInlineInvite() {
      setShowInlineInvite(false);
    }
    StoreContainer.InviteStore.onCloseInlineInvite(onCloseInlineInvite);

    function onDynamicButtonMounted() {
      setDynamicButtonContainer(
        StoreContainer.DynamicButtonStore.getContainer()
      );
    }
    StoreContainer.DynamicButtonStore.onMounted(onDynamicButtonMounted);

    function onSetQuickReplies(quickReplies: QuickReplyButton[]) {
      setQuickReplies(quickReplies);
    }
    StoreContainer.BotStore.onSetQuickReplies(onSetQuickReplies);

    function onSetDataField(dataField: DataField) {
      setDataField(dataField);
    }
    StoreContainer.BotStore.onSetDataField(onSetDataField);

    function onSetForm(form: Form) {
      setForm(form);
    }
    StoreContainer.BotStore.onSetForm(onSetForm);

    function onReceiveActionPopoverContent(content: ActionPopoverContent) {
      setActionPopoverContent(content);
    }
    StoreContainer.ActionCardStore.onReceiveActionPopoverContent(
      onReceiveActionPopoverContent
    );

    function onReceivePopoverInvite(popoverInvite: PopoverInvite) {
      setPopoverInvite(popoverInvite);
    }
    StoreContainer.InviteStore.onReceivePopoverInvite(onReceivePopoverInvite);

    function onReceiveInlineImageInvite(inlineImageInvite: InlineImageInvite) {
      setInlineImageInvite(inlineImageInvite);
    }
    StoreContainer.InviteStore.onReceiveInlineImageInvite(
      onReceiveInlineImageInvite
    );

    function onReceiveInlineChatInvite(inlineChatInvite: InlineChatInvite) {
      setInlineChatInvite(inlineChatInvite);
    }
    StoreContainer.InviteStore.onReceiveInlineChatInvite(
      onReceiveInlineChatInvite
    );

    function onCobrowseUpdate(state: string) {
      setCobrowseState(state);
    }
    StoreContainer.CobrowseStore.onCobrowseUpdate(onCobrowseUpdate);

    StoreContainer.PostMessageStore.bind();

    return () => {
      StoreContainer.FrameStore.removeAllListeners();
      StoreContainer.VisitorStore.removeAllListeners();
      StoreContainer.VisitorStore.unbindEvents();
      StoreContainer.EngagementStore.removeAllListeners();
      StoreContainer.ChatStore.removeAllListeners();
      StoreContainer.OAuthStore.removeAllListeners();
      StoreContainer.AvailabilityStore.stopAvailabilityPing();
      StoreContainer.PostMessageStore.unbind();
    };
  }, []);

  function showInviteWhenUnavailable(invite: any) {
    if (!invite.expression) {
      return false;
    }

    return invite.expression.indexOf("IsAvailable} = false") > -1;
  }
  //popover invite
  useEffect(() => {
    const shouldShowInvite =
      popoverInvite &&
      !chatIsAssigned &&
      (StoreContainer.AvailabilityStore.isChatAvailableInternal() ||
        showInviteWhenUnavailable(popoverInvite));

    setShowPopoverInvite(shouldShowInvite);
  }, [popoverInvite, chatIsAssigned]);

  //inline image invite
  useEffect(() => {
    const shouldShowInvite =
      inlineImageInvite &&
      !chatIsAssigned &&
      (StoreContainer.AvailabilityStore.isChatAvailableInternal() ||
        showInviteWhenUnavailable(inlineImageInvite));

    setShowInlineInvite(shouldShowInvite);
  }, [inlineImageInvite, chatIsAssigned]);

  //inline chat invite
  useEffect(() => {
    const shouldShowInvite =
      inlineChatInvite &&
      !chatIsAssigned &&
      (StoreContainer.AvailabilityStore.isChatAvailableInternal() ||
        showInviteWhenUnavailable(inlineChatInvite));

    setShowInlineInvite(shouldShowInvite);
  }, [inlineChatInvite, chatIsAssigned]);

  //chat stage events
  useEffect(() => {
    if (
      previousChatStage === ChatUIStage.Queued &&
      chatStage !== ChatUIStage.Queued
    ) {
      postTrackableEvent(EventTrackingType.VisitorExitedQueue);
    }
  }, [chatStage, previousChatStage]);

  //debug
  useEffect(() => {
    // Expose props to the Velaro API for debugging purposes.

    //todo: figure out how to add state to this now that we're using hooks
    //(window.parent.window as any).Velaro.getState = () => state;
    (window.parent.window as any).Velaro.getProps = () => props;
  }, [props]);

  //Invites and invite ping
  useEffect(() => {
    if (StoreContainer.EngagementStore.hasInvites()) {
      StoreContainer.VisitorStore.runInvites();

      if (StoreContainer.EngagementStore.areManualInvitesEnabled()) {
        StoreContainer.InviteStore.startManualInvitePing();
      }
    } else {
      console.log("skipping invite calls - site has no invites");
    }

    return () => {
      if (StoreContainer.EngagementStore.areManualInvitesEnabled()) {
        StoreContainer.InviteStore.stopManualInvitePing();
      }

      StoreContainer.InviteStore.destroyTimers();
    };
  }, []);

  //queue timer
  useEffect(() => {
    function updateQueueTimer() {
      if (chatStage !== ChatUIStage.Queued) {
        return;
      }
      setTimeInQueue((timeInQueue) => timeInQueue + 1);
    }

    const _queueIntervalRef = setInterval(() => updateQueueTimer(), 1000);

    return () => {
      clearInterval(_queueIntervalRef);
    };
  }, [chatStage]);

  //start an engagement, a call, or load landing widgets
  useEffect(() => {
    async function performExpandAction() {
      const isPopout = StoreContainer.FrameStore.isPopOut();
      const hasLandingWidgets =
        StoreContainer.SettingStore.areLandingWidgetsEnabled();
      const cookiesDisabled = StoreContainer.SettingStore.areCookiesDisabled();
      const hasCallId = StoreContainer.CallStore.getCallId();
      const hasEngagementId = StoreContainer.EngagementStore.hasEngagementId();
      const hasPreEngagementId =
        StoreContainer.EngagementStore.hasPreEngagementId();
      const hasInviteAcceptMessage =
        StoreContainer.InviteStore.hasInviteAcceptMessage();

      const showLandingWidgets =
        hasLandingWidgets &&
        !isPopout &&
        !hasEngagementId &&
        !hasPreEngagementId &&
        !hasInviteAcceptMessage;

      if (isPopout || isExpanded) {
        if (showLandingWidgets) {
          setChatStage(ChatUIStage.LandingWidgets);
        } else if (hasCallId) {
          StoreContainer.CallStore.loadCall();
        } else {
          await StoreContainer.EngagementStore.startEngagement();
        }
      }

      setResumingChat(false);
    }

    performExpandAction();
  }, [isExpanded, setChatStage]);

  //render
  if (resumingChat) {
    return null;
  }

  return (
    <React.Fragment>
      <ConfigContext.Provider value={config}>
        <EndpointContext.Provider value={getEndpoints()}>
          {renderStyle()}
          {renderLauncherPortal()}
          {renderMessengerPortal()}
          {renderPopoverInvite()}
          {renderLauncherInvite()}
          {renderDynamicButton()}
          {renderCallButton()}
        </EndpointContext.Provider>
      </ConfigContext.Provider>
    </React.Fragment>
  );
}

export default App;
